import React, { useEffect, useState } from "react";
import "./MarkAttendance.css";
import { toast } from "react-toastify";
import axios from "./../../axios";
import DatePicker from "react-datepicker";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

function MarkAttendance() {
  const schoolId = JSON.parse(localStorage.getItem("schoolId"));
  const timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );
  const academicYearSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );
  const [selectedAcademicSessionId, setSelectedAcademicSessionId] =
    useState(null);
  const [selectedTimetableId, setSelectedTimetableId] = useState(null);
  const [classId, setClassId] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [showTable, setShowTable] = useState(false);
  const [students, setStudents] = useState([]);
  const [initialStudents, setInitialStudents] = useState([]);
  const [enableSubmitBtn, setEnableSubmitBtn] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [studentsStatus, setStudentsStatus] = useState([]);
  const [disableButton, setDisableButton] = useState(false);
  const [modifiedAttendanceList, setModifiedAttendanceList] = useState([]);

  // ✅ Loading states
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [isRefreshingSettings, setIsRefreshingSettings] = useState(false);

  // ✅ NEW: attendance edit permission
  const [
    allowTeacherEditSubmittedAttendance,
    setAllowTeacherEditSubmittedAttendance,
  ] = useState(true);

  const [dropdownLists, setDropdownLists] = useState({
    standards: null,
    sections: null,
  });
  const { standards, sections } = dropdownLists;
  const standardList = [];
  const sectionList = [];
  const CTRole_Information = JSON.parse(
    localStorage.getItem("role_as_class_teacher"),
  );
  const CoRole_Information = JSON.parse(
    localStorage.getItem("role_as_co_ordinator"),
  );
  const general_role = JSON.parse(localStorage.getItem("general_role"));

  /* ── ✅ Extract fetch settings logic into reusable function ── */
  const fetchAttendanceSettings = async () => {
    if (!schoolId) return;
    try {
      const res = await axios.get(
        `/api/v1/attendance/settings?schoolId=${schoolId}`,
      );
      // axios response shape: res.data = { status, data, message }
      const payload = res?.data?.data ?? res?.data;
      if (
        payload &&
        typeof payload.allowTeacherEditSubmittedAttendance === "boolean"
      ) {
        setAllowTeacherEditSubmittedAttendance(
          payload.allowTeacherEditSubmittedAttendance,
        );
        return payload.allowTeacherEditSubmittedAttendance;
      }
      return null;
    } catch (e) {
      console.error("Failed to load attendance settings:", e);
      return null;
    }
  };

  /* ── ✅ Initial fetch of attendance settings ── */
  useEffect(() => {
    if (!schoolId) return;
    let cancelled = false;

    (async () => {
      await fetchAttendanceSettings();
      if (cancelled) return;
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  /* ── ✅ Manual refresh handler ── */
  const handleRefreshSettings = async () => {
    setIsRefreshingSettings(true);
    try {
      const allowed = await fetchAttendanceSettings();
      await fetchData();
      if (allowed === null) {
        toast.error("Failed to refresh settings");
      } else if (allowed) {
        toast.success("Edit permission refreshed — you can edit now");
      } else {
        toast.info("Edit permission still disabled by admin");
      }
    } finally {
      setIsRefreshingSettings(false);
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleSubmit = async () => {
    // ✅ Guard: block submit if editing is disabled
    if (isEditBlocked) {
      toast.error("Editing submitted attendance is disabled");
      return;
    }

    if (!selectedDate) {
      toast.warning("Please select a date");
      return;
    }

    const isAllNotTaken =
      modifiedAttendanceList.length > 0 &&
      modifiedAttendanceList.every((student) => student.status === "Not Taken");

    try {
      if (isAllNotTaken) {
        const attendanceIds = modifiedAttendanceList
          .map((item) => item._id)
          .filter(Boolean);
        await axios.delete("/api/v1/attendance/delete", {
          data: {
            standardId: classId,
            sectionId: sectionId,
            attendanceDate: formatDate(selectedDate),
            studentIds: attendanceIds,
          },
        });

        toast.success("Attendance deleted successfully");
      } else {
        const attendanceData = {
          academicSessionId: academicYearSessionId,
          timetableSessionId: timeTableSessionId,
          standardId: classId,
          sectionId: sectionId,
          attendanceDate: formatDate(selectedDate),

          attendanceDetails: modifiedAttendanceList
            .filter(
              (student) =>
                student.attendance !== "Not Taken" &&
                student.status !== "Not Taken",
            )
            .map((student, index) => ({
              studentId: student.id || student?.studentId?.id,
              status:
                student?.status ||
                student.attendance ||
                studentsStatus[index]?.status,
              reason: "",
            })),
        };

        const response = await axios.post(
          "/api/v1/attendance/create",
          attendanceData,
        );

        if (response) {
          toast.success(response?.data?.message);
        } else {
          toast.error("Error submitting attendance");
        }
      }
    } catch (error) {
      toast.error("Error submitting attendance");
    } finally {
      fetchData();
    }
  };

  const fetchData = async () => {
    setIsFetchingData(true); // ✅ start loading
    try {
      let body = {
        academicSessionId: academicYearSessionId,
        timetableSessionId: timeTableSessionId,
        standardId: classId,
        sectionId: sectionId,
        attendanceDate: formatDate(selectedDate),
      };

      const postResponse = await axios.post(
        "/api/v1/attendance/getStudentAttendance",
        body,
      );
      const filterData = postResponse?.data?.data.reduce((acc, curr, index) => {
        if (curr.status !== "Not Taken") {
          acc.push({ ...curr, index });
        }
        return acc;
      }, []);

      setModifiedAttendanceList(filterData);
      if (postResponse?.data?.data?.length > 0) {
        setStudentsStatus(postResponse?.data?.data);
      } else {
        setStudentsStatus([]); // ✅ reset if empty
      }
      let getResponse = await axios.get(
        `/api/v1/attendance/getlist?academicYearSessionId=${academicYearSessionId}&timetableSessionId=${timeTableSessionId}&standardId=${classId}&sectionId=${sectionId}`,
      );

      setStudents(getResponse?.data?.data?.students || []);
      setInitialStudents(getResponse?.data?.data?.students || []);
    } catch (error) {
      console.error("Error fetching data");
    } finally {
      setIsFetchingData(false); // ✅ stop loading
    }
  };

  const handleClassChange = (value) => {
    setClassId(value);
    setIsModified(false);
    setShowTable(false);
    setSectionId(null);
    setDisableButton(false);
  };
  const handleSectionChange = (value) => {
    setSectionId(value);
    setIsModified(false);
    setShowTable(false);
    setDisableButton(false);
  };

  const handleChangeDate = (date) => {
    setShowTable(false);
    setSelectedDate(date);
    setDisableButton(false);
  };

  const handleTableUi = () => {
    setDisableButton(true);
    fetchData();
    setShowTable(true);
  };

  useEffect(() => {
    if (!classId || !sectionId || !selectedDate) {
      setEnableSubmitBtn(true);
    } else {
      setEnableSubmitBtn(false);
    }
  }, [
    selectedAcademicSessionId,
    selectedTimetableId,
    classId,
    sectionId,
    selectedDate,
  ]);

  const handleAttendanceChange = (id, status) => {
    setStudents((prevStudents) => {
      const updatedStudents = prevStudents.map((student) =>
        student.id === id
          ? { ...student, attendance: status, isEdited: true }
          : student,
      );

      const modifiedList = updatedStudents
        .filter(
          (student) => student.attendance && student.attendance !== "Not Taken",
        )
        .map((student) => ({ ...student }));

      setModifiedAttendanceList(modifiedList);

      return updatedStudents;
    });

    setIsModified(true);
  };

  const handleExistingStatusChange = (studentId, status) => {
    setModifiedAttendanceList((prevList) => {
      const existingStudent = prevList.findIndex(
        (item) => item.studentId?.id === studentId,
      );

      if (existingStudent > -1) {
        const updatedItem = { ...prevList[existingStudent], status };
        const newList = [...prevList];
        newList[existingStudent] = updatedItem;
        return newList;
      }

      const originalStudent = students.find(
        (s) => s.id === studentId || s._id === studentId,
      );

      if (originalStudent) {
        return [
          ...prevList,
          {
            id: originalStudent.id || originalStudent._id,
            studentId: originalStudent,
            reason: "",
            status: status,
          },
        ];
      }

      return prevList;
    });

    setIsModified(true);
  };

  let sectionArr = [];
  let standardArr = [];
  const dropDownHelper = () => {
    function addUniqueObjectToArraystand(obj) {
      if (
        !standardArr.some(
          (item) => JSON.stringify(item) === JSON.stringify(obj),
        )
      ) {
        standardArr.push(obj);
      }
    }
    CTRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
    CoRole_Information?.map((obj) =>
      addUniqueObjectToArraystand(obj.standard_id),
    );
  };

  dropDownHelper();

  const handleDropDownSec = () => {
    function addUniqueObjectToArraysect(obj) {
      if (
        !sectionArr.some((item) => JSON.stringify(item) === JSON.stringify(obj))
      ) {
        sectionArr.push(obj);
      }
    }
    CTRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === classId) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
    CoRole_Information?.map((obj) => {
      if (obj?.standard_id?.id === classId) {
        addUniqueObjectToArraysect(obj.section_id);
      }
    });
  };
  handleDropDownSec();

  const studentStatus = studentsStatus.map((sts) => sts.status);

  const studentEditStatus = modifiedAttendanceList.map((sts) => sts.status);
  let allStatus = ["Present", "Absent", "Delay", "Leave"];
  const hasValidStatus = studentStatus.some((status) =>
    allStatus.includes(status),
  );

  /* ✅ Edit blocked only when attendance is already submitted AND setting says NO */
  const isEditBlocked = hasValidStatus && !allowTeacherEditSubmittedAttendance;

  const totalCount = students?.length || 0;

  const presentCount =
    studentStatus?.reduce(
      (total, status) =>
        total + (status === "Present" || status === "Delay" ? 1 : 0),
      0,
    ) || 0;

  const totalPercentage =
    totalCount > 0 ? Math.floor((presentCount / totalCount) * 100) : 0;

  const handleMarkAllPresent = () => {
    if (isEditBlocked) return; // ✅ guard

    const updatedStudents = students.map((student) => ({
      ...student,
      attendance: "Present",
      isEdited: true,
    }));

    setStudents(updatedStudents);

    const modifiedList = updatedStudents.map((student) => ({
      id: student.id || student._id,
      studentId: student,
      status: "Present",
      reason: "",
    }));

    setModifiedAttendanceList(modifiedList);

    setIsModified(true);
  };

  return (
    <div className="attendance-container">
      <div className="card ">
        <h2 className="card-header">Mark Attendance</h2>

        <div className="form-container">
          <div className="form-group">
            <FormControl size="small" fullWidth>
              <InputLabel>Class*</InputLabel>
              <Select
                label="Class*"
                value={classId || ""}
                onChange={(e) => handleClassChange(e.target.value)}
              >
                <MenuItem value="null">Select Class</MenuItem>
                {standardArr?.length > 0 &&
                  [...standardArr]
                    .sort((a, b) => {
                      const aNum = Number(a.name);
                      const bNum = Number(b.name);

                      const bothNumeric = !isNaN(aNum) && !isNaN(bNum);
                      if (bothNumeric) {
                        return aNum - bNum;
                      }

                      return a.name.localeCompare(b.name);
                    })
                    .map((standard, index) => (
                      <MenuItem
                        key={index}
                        value={standard?.id}
                        name={standard?.name}
                      >
                        {standard?.name}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </div>

          <div className="form-group">
            <FormControl size="small" fullWidth>
              <InputLabel>Section*</InputLabel>
              <Select
                label="Section*"
                value={sectionId || ""}
                onChange={(e) => handleSectionChange(e.target.value)}
              >
                <MenuItem value="null">Select Section</MenuItem>
                {sectionArr
                  ?.slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>

          <div className="form-group">
            <DatePicker
              selected={selectedDate}
              onChange={(date) => handleChangeDate(date)}
              dateFormat="dd/MM/yyyy"
              placeholderText="Choose a date"
              className="form-control custom-width"
              popperProps={{
                modifiers: [
                  {
                    name: "zIndex",
                    enabled: true,
                    phase: "beforeWrite",
                    fn({ state }) {
                      state.styles.popper.zIndex = 9999999;
                    },
                  },
                ],
              }}
            />
          </div>
          <div className="mb-3">
            <button
              className="list-button "
              style={{
                background: disableButton && "disable",
                width: "70px",
              }}
              disabled={
                (showTable && true) || !sectionId || !classId || !selectedDate
              }
              onClick={() => {
                handleTableUi();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {showTable && (
        <div className="">
          <div className="attendanceDate">
            <div className="totalAttendance">
              Overall Class Attendance :{" "}
              <span>
                {" "}
                {totalPercentage}% [{presentCount}/{totalCount}]
              </span>{" "}
            </div>
            <div className="todayDate">
              <div>
                {selectedDate ? "Date -" : ""}
                <span className="text-muted-foreground">
                  {" "}
                  {selectedDate
                    ? new Date(selectedDate).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : ""}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {/* ✅ Refresh settings button */}
                <button
                  className="list-button"
                  onClick={handleRefreshSettings}
                  disabled={isRefreshingSettings}
                  title="Refresh edit permission from admin settings"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    background: "#fff",
                    color: "#0d6efd",
                    border: "1px solid #0d6efd",
                  }}
                >
                  {isRefreshingSettings ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                  Refresh
                </button>

                <button
                  className="list-button"
                  onClick={handleMarkAllPresent}
                  disabled={isEditBlocked}
                  title={
                    isEditBlocked
                      ? "Editing submitted attendance is disabled"
                      : ""
                  }
                >
                  Mark All Present
                </button>
              </div>
            </div>
          </div>

          {/* ✅ Locked banner */}
          {isEditBlocked && (
            <div
              style={{
                margin: "8px 0",
                padding: "8px 12px",
                border: "1px solid #f59e0b",
                background: "#fef3c7",
                color: "#92400e",
                borderRadius: "6px",
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span>
                🔒 Submitted attendance is <b>locked</b>. Editing is disabled by
                admin. Contact your administrator to make changes.
              </span>
              <button
                onClick={handleRefreshSettings}
                disabled={isRefreshingSettings}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "4px 10px",
                  border: "1px solid #92400e",
                  background: "transparent",
                  color: "#92400e",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: isRefreshingSettings ? "not-allowed" : "pointer",
                }}
              >
                {isRefreshingSettings ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <RefreshIcon fontSize="small" />
                )}
                Check again
              </button>
            </div>
          )}

          <div
            className="table-container "
            style={{ overflowY: "scroll", maxHeight: "calc(100vh - 410px)" }}
          >
            <table>
              <thead
                style={{
                  backgroundColor: "#ffd8be",
                  position: "sticky",
                  top: "0",
                  zIndex: "1",
                }}
              >
                <tr style={{ textAlign: "center" }}>
                  <th>Sr.No.</th>
                  <th>Roll Code</th>
                  <th>Admission No</th>
                  <th>Name</th>
                  <th style={{ color: "blue" }}>Present</th>
                  <th style={{ color: "red" }}>Absent</th>
                  <th style={{ color: "orange" }}>Delay</th>
                  <th style={{ color: "green" }}>Leave</th>
                  <th style={{ color: "gray" }}>Not Taken</th>
                </tr>
              </thead>

              {/* ✅ Loader while fetching */}
              {isFetchingData ? (
                <tbody>
                  <tr>
                    <td colSpan="9" style={{ padding: "40px 0" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "12px",
                        }}
                      >
                        <CircularProgress size={32} />
                        <span style={{ fontSize: "14px", color: "#666" }}>
                          Loading students...
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : !hasValidStatus ? (
                <tbody>
                  {students.length > 0 ? (
                    students.map((student, index) => {
                      const studentId = student.id || index;
                      let allStatus = [
                        "Present",
                        "Absent",
                        "Delay",
                        "Leave",
                        "Not Taken",
                      ];
                      const selectedStatus =
                        student.attendance || studentStatus[index];

                      return (
                        <tr
                          key={studentId}
                          style={{
                            textAlign: "center",
                            backgroundColor:
                              (index + 1) % 2 === 0 ? "#ececec" : "white",
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>{student.code || "-"}</td>
                          <td>{student.addmissionNo || "-"}</td>
                          <td>{student.name}</td>
                          {allStatus.map((status) => (
                            <td key={status}>
                              <div
                                className={`radio-container ${
                                  selectedStatus === status ? "selected" : ""
                                }`}
                                style={{
                                  cursor: isEditBlocked
                                    ? "not-allowed"
                                    : "pointer",
                                  opacity: isEditBlocked ? 0.6 : 1,
                                }}
                                onClick={() => {
                                  if (isEditBlocked) return;
                                  handleAttendanceChange(studentId, status);
                                }}
                              >
                                <input
                                  type="radio"
                                  name={`attendance-${studentId}`}
                                  value={selectedStatus || ""}
                                  id={`${studentId}-${status}`}
                                  checked={selectedStatus === status}
                                  disabled={isEditBlocked}
                                  onChange={() =>
                                    handleAttendanceChange(studentId, status)
                                  }
                                />
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="nofoundStud" colSpan="9">
                        No students found
                      </td>
                    </tr>
                  )}
                </tbody>
              ) : (
                <tbody>
                  {students.length > 0 ? (
                    students.map((student, index) => {
                      const studentId = student.id || index;
                      let allStatus = [
                        "Present",
                        "Absent",
                        "Delay",
                        "Leave",
                        "Not Taken",
                      ];
                      const modifiedStudent = modifiedAttendanceList.find(
                        (s) =>
                          s.id === student.id || s.studentId?.id === student.id,
                      );

                      const selectedStatus =
                        modifiedStudent?.status ||
                        student.attendance ||
                        "Not Taken";
                      const studentReason = modifiedStudent?.reason;

                      return (
                        <tr
                          key={studentId}
                          style={{
                            textAlign: "center",
                            backgroundColor:
                              (index + 1) % 2 === 0 ? "#ececec" : "white",
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>{student.code || "-"}</td>
                          <td>{student.addmissionNo || "-"}</td>
                          <td>{student.name}</td>
                          {allStatus.map((status) => (
                            <td key={status}>
                              <div
                                className={`radio-container ${
                                  selectedStatus === status ? "selected" : ""
                                }`}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "4px",
                                  cursor: isEditBlocked
                                    ? "not-allowed"
                                    : "pointer",
                                  opacity: isEditBlocked ? 0.6 : 1,
                                }}
                                onClick={() => {
                                  if (isEditBlocked) return;
                                  handleExistingStatusChange(studentId, status);
                                }}
                              >
                                <Tooltip
                                  title={
                                    selectedStatus === status &&
                                    (status === "Leave" || status === "Delay")
                                      ? studentReason
                                      : null
                                  }
                                  arrow
                                  placement="top"
                                  enterDelay={200}
                                  leaveDelay={100}
                                >
                                  <input
                                    type="radio"
                                    name={`attendance-${studentId}`}
                                    value={selectedStatus || ""}
                                    id={`${studentId}-${status}`}
                                    checked={selectedStatus === status}
                                    disabled={isEditBlocked}
                                    onChange={() =>
                                      handleExistingStatusChange(
                                        studentId,
                                        status,
                                      )
                                    }
                                  />
                                </Tooltip>
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="nofoundStud" colSpan="9">
                        No students found
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>

          <div
            className="position-sticky bottom-0 bg-white p-2 border-top"
            style={{ zIndex: 10 }}
          >
            <div
              className="d-flex justify-content-end gap-2 align-items-center"
              style={{ gap: "12px" }}
            >
              {isEditBlocked && (
                <div
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #dc2626",
                    background: "#fee2e2",
                    color: "#991b1b",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  🔒 You are <b>not allowed</b> to edit already taken
                  attendance.
                </div>
              )}

              <button
                className="list-button-submit"
                onClick={handleSubmit}
                disabled={!isModified || isEditBlocked || isFetchingData}
                title={
                  isEditBlocked
                    ? "Editing submitted attendance is disabled by admin"
                    : ""
                }
                style={{ width: "20%" }}
              >
                Submit Attendance
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarkAttendance;
